/* --------------------------------------------------------------------------
   Kortbetalningar och kortterminalens kvitto

   Med en integrerad kortterminal har exporten, utöver kassans betalningsrad,
   terminalens eget kvitto per kortbetalning i cardTransactionReceipts:

     { rowIndex, cardTransactionReceiptType: 'CUSTOMER' | 'MERCHANT',
       referenceId, text }

   text är kvittot som skrevs ut i terminalen, rad för rad. Ur det läses tre
   saker, och inget annat:
     • korttypen, raden med kortets namn följt av maskerat kortnummer:
         "Debit Mastercard          ***1234"
     • om köpet var kontaktlöst, raden som börjar med "Kontaktlös"
     • om kunden slog personlig kod, raden "Personlig kod"

   Beloppet tas inte ur texten utan från kassans betalningsrad, så att
   kortöversikten alltid summerar till Kort under Betalsätt. Kortnumrets fyra
   sista siffror, referenser och resten av texten sparas aldrig: normalize()
   behåller bara korttyp och de två markeringarna.

   Formatet ovan är det som exporter med integrerad terminal har. En terminal
   som skriver kvittot på annat sätt ger "Okänt kort" i stället för en gissning,
   och kontaktlöst och personlig kod redovisas bara när filen faktiskt har
   sådana markeringar.

   MERCHANT är butikens exemplar av samma transaktion (samma referenceId som
   kundens) och räknas inte, annars blev varje sådan betalning dubbel.

   Kvitto och betalning hör ihop genom rowIndex. Har en betalning inget
   terminalkvitto med samma rowIndex, men kvittot har exakt en sådan betalning
   och ett sådant terminalkvitto kvar, hör de ihop ändå. Annars redovisas
   betalningen som "utan terminalkvitto".
   -------------------------------------------------------------------------- */

const CARD_LINE   = /^(.+?)\s{2,}\*{2,}\d{4}$/;
const CONTACTLESS = /^Kontaktlös\b/;
const PIN         = /^Personlig kod$/;

/**
 * Läser korttyp och markeringar ur terminalkvittots text. label är null när
 * ingen rad med kortnamn finns — då är formatet okänt.
 */
export function parseTerminalReceipt(text){
  const lines = typeof text === 'string'
    ? text.split(/\r?\n/).map(l => l.trim()).filter(Boolean) : [];
  let label = null, contactless = false, pin = false;
  for(const l of lines){
    const m = label === null && l.match(CARD_LINE);
    if(m) label = m[1].replace(/\s+/g, ' ').trim();
    if(CONTACTLESS.test(l)) contactless = true;
    if(PIN.test(l)) pin = true;
  }
  return {label, contactless, pin};
}

/**
 * Kassans kortbetalningar på ett kvitto, var och en med uppgifterna ur sitt
 * terminalkvitto: [{amount, terminal, label, contactless, pin}].
 *   terminal  false när betalningen saknar terminalkvitto
 *   label     korttypen, null när den inte gick att läsa
 */
export function cardPaymentsOf(r){
  const payments = (r.receiptPayments || [])
    .filter(p => p.receiptPaymentType === 'CARD' && !p.moneyBack);
  if(!payments.length) return [];

  const free = (r.cardTransactionReceipts || [])
    .filter(c => c && c.cardTransactionReceiptType === 'CUSTOMER');
  const paired = payments.map(p => {
    const i = free.findIndex(c => c.rowIndex === p.rowIndex);
    return i < 0 ? null : free.splice(i, 1)[0];
  });
  const open = paired.flatMap((c, i) => c ? [] : [i]);
  if(open.length === 1 && free.length === 1) paired[open[0]] = free[0];

  return payments.map((p, i) => {
    const amount = Number(p.amount) || 0;
    if(!paired[i]) return {amount, terminal: false, label: null, contactless: false, pin: false};
    return {amount, terminal: true, ...parseTerminalReceipt(paired[i].text)};
  });
}

/** Nyckel som slår ihop samma korttyp med olika skiftläge: "MASTERCARD" och "Mastercard". */
export const cardKey = label => label === null ? '' : label.toLocaleLowerCase('sv');
